import { Link } from "react-router-dom";
import { LOGO_URL } from "@/config/branding";
import { Trash2, Mail, AlertTriangle, ShieldCheck } from "lucide-react";

export default function DeleteAccountPage() {
  return (
    <div className="min-h-screen flex flex-col" style={{ backgroundColor: '#F5F5F7' }} data-testid="delete-account-page">
      {/* Nav */}
      <nav className="bg-white/95 backdrop-blur-md border-b border-[#E5E5EA] sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-3 flex items-center justify-between">
          <Link to="/">
            <img src={LOGO_URL} alt="Self Certify UK" className="h-12 sm:h-14 object-contain object-left" />
          </Link>
          <div className="flex items-center gap-3 sm:gap-4">
            <Link to="/" className="text-sm font-medium hover:text-[#00A3E0] transition-colors" style={{ fontFamily: 'Outfit, sans-serif', color: '#8E8E93' }}>
              Home
            </Link>
            <Link to="/login" className="bg-[#00A3E0] text-white text-sm font-semibold px-5 py-2.5 rounded-xl hover:bg-[#0082B3] transition-all" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="nav-sign-in">
              Sign In
            </Link>
          </div>
        </div>
      </nav>

      <div className="flex-1 max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <h1 className="text-3xl sm:text-4xl font-bold tracking-tight mb-2" style={{ fontFamily: 'Outfit, sans-serif', color: '#1C1C1E' }} data-testid="delete-account-title">
          Delete Your Account
        </h1>
        <p className="text-sm mb-10" style={{ color: '#8E8E93' }}>
          You can permanently delete your Self Certify UK account and all associated data at any time.
        </p>

        <div className="space-y-4">
          {/* In the App */}
          <div className="bg-white rounded-2xl p-6" style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }} data-testid="delete-method-app">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ backgroundColor: '#FEECEC' }}>
                <Trash2 className="w-5 h-5 text-[#EF4444]" />
              </div>
              <h2 className="text-lg font-bold" style={{ fontFamily: 'Outfit, sans-serif', color: '#1C1C1E' }}>In the Mobile App (fastest)</h2>
            </div>
            <ol className="list-decimal list-inside space-y-2 text-sm" style={{ color: 'var(--text-primary)' }}>
              <li>Open the Self Certify UK app and sign in</li>
              <li>Go to <strong>Profile</strong></li>
              <li>Tap <strong>Delete Account</strong> and confirm</li>
            </ol>
            <p className="text-xs mt-4" style={{ color: 'var(--text-secondary)' }}>
              This permanently deletes your account, profile/contractor details, and all saved certificate data.
            </p>
          </div>

          {/* By Email */}
          <div className="bg-white rounded-2xl p-6" style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }} data-testid="delete-method-email">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ backgroundColor: '#E8F7FC' }}>
                <Mail className="w-5 h-5 text-[#00A3E0]" />
              </div>
              <h2 className="text-lg font-bold" style={{ fontFamily: 'Outfit, sans-serif', color: '#1C1C1E' }}>By Email</h2>
            </div>
            <p className="text-sm" style={{ color: 'var(--text-primary)' }}>
              If you can't access the app, email{" "}
              <a href="mailto:selfcertifyuk@gmail.com" className="text-[#00A3E0] font-semibold hover:underline">selfcertifyuk@gmail.com</a>{" "}
              from your registered email address with the subject <strong>"Delete my account"</strong>, and we'll delete your account and data within 30 days.
            </p>
          </div>

          {/* What's Deleted */}
          <div className="bg-white rounded-2xl p-6" style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }} data-testid="delete-what-deleted">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ backgroundColor: '#FFF7ED' }}>
                <AlertTriangle className="w-5 h-5 text-[#F59E0B]" />
              </div>
              <h2 className="text-lg font-bold" style={{ fontFamily: 'Outfit, sans-serif', color: '#1C1C1E' }}>What's Deleted</h2>
            </div>
            <ul className="list-disc list-inside space-y-1 text-sm" style={{ color: 'var(--text-primary)' }}>
              <li>Your account and login credentials</li>
              <li>Contractor and profile details</li>
              <li>All stored certificates and notification records</li>
              <li>Saved templates and draft data</li>
            </ul>
          </div>

          {/* What May Be Retained */}
          <div className="bg-white rounded-2xl p-6" style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }} data-testid="delete-what-retained">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ backgroundColor: '#EAFBF0' }}>
                <ShieldCheck className="w-5 h-5 text-[#22C55E]" />
              </div>
              <h2 className="text-lg font-bold" style={{ fontFamily: 'Outfit, sans-serif', color: '#1C1C1E' }}>What May Be Retained</h2>
            </div>
            <p className="text-sm" style={{ color: 'var(--text-primary)' }}>
              We may keep limited transaction and billing records where required by law (e.g., for tax/accounting purposes), for the minimum period required.
            </p>
          </div>
        </div>

        <div className="flex gap-3 mt-8">
          <Link to="/" className="px-6 py-2.5 text-sm font-semibold rounded-xl border border-[#E5E5EA] hover:bg-gray-50 transition-all" style={{ color: '#1C1C1E' }} data-testid="back-to-home-btn">Back to Home</Link>
          <Link to="/login" className="bg-[#00A3E0] text-white px-6 py-2.5 text-sm font-semibold rounded-xl hover:bg-[#0082B3] transition-all" data-testid="sign-in-btn">Sign In</Link>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-[#1C1C1E] py-8 mt-auto">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
            <img src={LOGO_URL} alt="Self Certify UK" className="h-8 object-contain" />
            <div className="flex items-center gap-6 text-sm text-white/40">
              <Link to="/" className="hover:text-white transition-colors">Home</Link>
              <Link to="/contact" className="hover:text-white transition-colors">Contact</Link>
              <Link to="/privacy" className="hover:text-white transition-colors">Privacy Policy</Link>
            </div>
          </div>
          <div className="mt-6 pt-4 border-t border-white/10 text-center text-xs text-white/30">
            &copy; {new Date().getFullYear()} Self Certify UK. All rights reserved.
          </div>
        </div>
      </footer>
    </div>
  );
}
